import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import EventHoursInputOptions from './EventHoursInputOptions';
import {
  DURATION_TYPES,
  getEventHoursLabel
} from 'v1/helpers/byModel/EventHelper';

import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { cn } from 'v5/platform/dom/cn';

import './EventHoursInput.scss';
import { inputVariants } from 'v5/design-system/Input.js';
import { getV5Props } from '../../../../byType/text/GetV5Props.js';

const EventHoursInput = props => {
  const { t } = useTranslation();

  // Handle change events and propagate them upward
  const handleChange = (field, value, previous) =>
    props.onChange(field, value, previous);

  return (
    <div
      className={classnames([
        'EventHoursInput',
        'tailwind-root',
        {
          [`EventHoursInput-${props.size}`]: props.size,
          [`EventHoursInput-${props.appearance}`]: props.appearance
        },
        props.className
      ])}
      style={props.size === 'L' ? { alignSelf: 'flex-start' } : {}}
    >
      <Popover>
        <div
          className={classnames(
            { 'text-13-700': props.size === 'S' },
            { 'emulate-h3': props.size === 'M' },
            props.size === 'L' &&
              '[&_input]:py-3! [&_input]:leading-9! [&_label]:mb-[5px]! [&_input]:text-lg!'
          )}
        >
          <label className="form-label">
            {t('v1_shared:EventHoursInput.hoursLabel')}
          </label>
          <PopoverTrigger asChild disabled={props.disabled}>
            <button
              className={cn(
                inputVariants(getV5Props(props)),
                'text-left min-w-32'
              )}
              disabled={props.disabled}
            >
              {getEventHoursLabel({ ...props }, t)}
            </button>
          </PopoverTrigger>
        </div>

        <PopoverContent
          className="p-0 w-90 [&_.RadioGroup>*,&_.RadioGroup]:mb-0! [&_.RadioGroup>*]:p-5 [&_.Radio:not(:first-child)]:border-t [&_.Radio]:border-border-default"
          align="start"
          side="bottom"
        >
          <EventHoursInputOptions
            handleChange={handleChange}
            durationType={props.date_type}
            estimatedDailyHours={props.estimated_daily_hours}
            estimatedDailyMinutes={props.estimated_daily_minutes}
            estimatedStartTime={props.estimated_start_time}
            estimatedEndTime={props.estimated_end_time}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const TypeEventHours = {
  date_type: PropTypes.oneOf([
    DURATION_TYPES.RECURRING_DEFAULT,
    DURATION_TYPES.RECURRING_HOURS,
    DURATION_TYPES.RECURRING_SPECIFIC_HOURS,
    DURATION_TYPES.INCLUSIVE_HOURS
  ]),
  estimated_daily_hours: PropTypes.number, // Only for RECURRING_HOURS
  estimated_daily_minutes: PropTypes.number, // Only for RECURRING_HOURS
  estimated_start_time: PropTypes.any, // '09:00:00' / Only for RECURRING_SPECIFIC_HOURS
  estimated_end_time: PropTypes.any // '14:00:00' / Only for RECURRING_SPECIFIC_HOURS
};

EventHoursInput.propTypes = {
  className: PropTypes.string,
  appearance: PropTypes.oneOf(['outline', 'silent']),
  size: PropTypes.oneOf(['XS', 'S', 'M']),
  disabled: PropTypes.bool,
  ...TypeEventHours,
  onChange: PropTypes.func
};

EventHoursInput.defaultProps = {
  estimated_daily_hours: 8,
  estimated_daily_minutes: 0
};

export default EventHoursInput;
